import { Link } from '@tanstack/react-router'
import { ArrowRight, ShieldAlert } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { PageHeader } from '@/components/page-header'
import { RequiresWiderScreen } from '@/components/requires-wider-screen'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ADMIN_PERMISSIONS, useAdminPermissions } from '@/features/admin/permissions'
import { useAdminRoles } from '@/features/admin/queries'

/**
 * The role register.
 *
 * Read-only here — creating a role from nothing has no screen in this pass, see
 * `AdminCapabilityGaps` on the module's landing page. Every existing role is listed with its
 * member count and permission count; the permissions themselves, grouped by module, are on the
 * role's own detail screen.
 */
export function AdminRolesPage() {
  const permissions = useAdminPermissions()
  const query = useAdminRoles()

  return (
    <RequiresWiderScreen
      screen="Roles"
      reason="A role's permissions are read and edited together here, against the full catalogue — this is a desk screen."
    >
      <PageHeader title="Roles" description="What each role grants, grouped by module, and who holds it." />

      {!permissions.canViewRoles ? (
        <div className="flex flex-col items-center gap-2 rounded-card border border-border-subtle bg-surface px-4 py-12 text-center">
          <ShieldAlert className="size-6 text-warning-text" aria-hidden />
          <p className="text-sm font-medium text-heading">You cannot read the role register</p>
          <p className="max-w-lg text-xs text-muted">
            This needs <span className="font-mono">{ADMIN_PERMISSIONS.roleView}</span>.
          </p>
        </div>
      ) : query.isPending ? (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full" />
          ))}
        </div>
      ) : query.isError ? (
        <ErrorSurface error={query.error} onRetry={() => void query.refetch()} />
      ) : query.data.length === 0 ? (
        <p className="text-sm text-muted">No role is defined.</p>
      ) : (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {query.data.map((role) => (
            <Link key={role.roleId} to="/admin/roles/$roleId" params={{ roleId: role.roleId }} className="block">
              <Card className="flex h-full flex-col gap-2 p-4 transition-colors hover:border-border-strong hover:bg-surface-hover">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium text-heading">{role.name}</p>
                  <ArrowRight className="mt-0.5 size-4 shrink-0 text-muted" aria-hidden />
                </div>
                <p className="text-xs text-body">{role.description}</p>
                <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-1">
                  <Badge tone="neutral">
                    {role.userCount} member{role.userCount === 1 ? '' : 's'}
                  </Badge>
                  <Badge tone="neutral">
                    {role.permissions.length} permission{role.permissions.length === 1 ? '' : 's'}
                  </Badge>
                  {role.isSystemRole ? <Badge tone="brand">System</Badge> : null}
                  {role.forbidsFinancialPermissions ? <Badge tone="warning">Non-financial only</Badge> : null}
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </RequiresWiderScreen>
  )
}
